import { Component, HostListener, OnDestroy, computed, inject, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { HeaderBeforeLoginService } from '@services/services/header-before-login.service';
import { LanguageService, SupportedLanguage } from '@services/language.service';
import { SignService } from '@services/sign.service';
import { SignToolbarService, SignToolbarAction, SignToolbarListItem } from '@services/sign-toolbar.service';
import { EnumSignTagTypes, EnumSignerRoleType } from '@modals/app.modal';
import { Subscription } from 'rxjs';
import { PdfEditStateService, PdfEditToolType } from '@services/pdf-edit-state.service';
import { TableService } from '@services/table.service';
import { MatButtonModule } from '@angular/material/button';

@Component({
  selector: 'kzn-header-before-login',
  standalone: true,
  imports: [CommonModule, MatCardModule, MatIconModule, MatButtonModule, MatTooltipModule],
  templateUrl: './header-before-login.component.html',
  styleUrl: './header-before-login.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class HeaderBeforeLoginComponent implements OnDestroy {
  private _headerBeforeLoginService = inject(HeaderBeforeLoginService);
  private _languageService = inject(LanguageService);
  private _signService = inject(SignService);
  private _signToolbarService = inject(SignToolbarService);
  private _tableService = inject(TableService);

  pdfEditState = inject(PdfEditStateService);

  private _toolbarSubscription: Subscription = new Subscription();

  public isSignToolbarActive: boolean = false;
  public isMobileView: boolean = false;
  public isMobileToolbarExpanded: boolean = false;
  public signerRole: EnumSignerRoleType | null = null;
  public hoveredToolbarKey: string | null = null;
  public currentLanguage: SupportedLanguage = 'en';

  signerNameInitials = computed(() => this._headerBeforeLoginService.userInitials());
  headerLogoReady = computed(() => this._headerBeforeLoginService.headerLogoLoaded());
  headerLogoSrc = computed(() => {
    return this._headerBeforeLoginService.headerLogo() || 'assets/logo2.png';
  });
  showViewBanner = computed(() => this._headerBeforeLoginService.showViewBanner());
  showHeader = computed(() => this._headerBeforeLoginService.showHeader());
  isCustomerLayout = computed(() => this._headerBeforeLoginService.isCustomerLayout());
  kapybaraRevealed = this._headerBeforeLoginService.kapybaraRevealed;

  constructor() {
    this.updateMobileViewState();
    this.initializeLanguageFromRegion();
    this.logCountryFromGeolocation();
    this._toolbarSubscription.add(
      this._languageService.language$.subscribe((lang) => {
        this.currentLanguage = lang;
      })
    );
    this._toolbarSubscription.add(
      this._signToolbarService.active$.subscribe((active) => {
        this.isSignToolbarActive = active;
      })
    );
    this._toolbarSubscription.add(
      this._signToolbarService.expanded$.subscribe((expanded) => {
        this.isMobileToolbarExpanded = expanded;
      })
    );
    this._toolbarSubscription.add(
      this._signToolbarService.signerRole$.subscribe((role) => {
        this.signerRole = role;
      })
    );
  }

  @HostListener('window:resize')
  onWindowResize() {
    this.updateMobileViewState();
  }

  setLanguage(lang: SupportedLanguage) {
    this._languageService.setLanguage(lang);
  }

  onLanguageChange(event: Event) {
    const target = event.target as HTMLSelectElement | null;
    const allowed: SupportedLanguage[] = ['en','nl','es','fr','de','hi'];
    const value = (target?.value as SupportedLanguage) ?? 'en';
    this.setLanguage(allowed.includes(value) ? value : 'en');
  }

  onKapybaraClick(): void {
    this._headerBeforeLoginService.onKapybaraHeaderClick();
  }

  isAllReviewers(): boolean {
    return (this._tableService.tableInputData?.length ?? 0) > 0 &&
      this._tableService.tableInputData.every((d: any) => (d.role || '').toLowerCase() === EnumSignerRoleType.REVIEWER.toLowerCase());
  }

  onHeaderToolClick(tool: PdfEditToolType): void {
    this.pdfEditState.toggleTool(tool);
  }

  private static readonly TOOL_ICONS: Record<string, string> = {
    esign: 'edit', initials: 'short_text', stamp: 'image',
    text: 'text_fields', date: 'calendar_today', checkbox: 'check_box', comment: 'comment'
  };

  onHeaderToolDragStart(event: DragEvent, tool: PdfEditToolType): void {
    if (!event.dataTransfer) return;
    event.dataTransfer.setData('text/plain', tool);
    event.dataTransfer.effectAllowed = 'copy';
    const labels: Record<string, string> = { esign: 'Signature', initials: 'Initials', stamp: 'Stamp', text: 'Text', date: 'Date', checkbox: 'Checkbox', comment: 'Comment' };
    const dragEl = document.createElement('div');
    dragEl.style.cssText = 'position:absolute;top:-9999px;left:0;display:inline-flex;align-items:center;gap:6px;padding:8px 12px;background:#fff;border:1px solid #e5e7eb;border-radius:6px;font-size:14px;color:#4b5563;white-space:nowrap;pointer-events:none;';
    const iconEl = document.createElement('span');
    iconEl.className = 'material-icons';
    iconEl.textContent = HeaderBeforeLoginComponent.TOOL_ICONS[tool] ?? 'drag_indicator';
    iconEl.style.cssText = 'font-size:18px;width:18px;height:18px;line-height:18px;overflow:hidden;flex-shrink:0;';
    const labelEl = document.createElement('span');
    labelEl.textContent = labels[tool] ?? tool;
    labelEl.style.cssText = 'line-height:1;';
    dragEl.appendChild(iconEl);
    dragEl.appendChild(labelEl);
    document.body.appendChild(dragEl);
    event.dataTransfer.setDragImage(dragEl, 0, 0);
    setTimeout(() => dragEl.remove(), 0);
  }

  private initializeLanguageFromRegion(): void {
    const locale = navigator.language || 'en';
    const langCode = locale.split('-')[0]?.toLowerCase();
    const langMap: Record<string, SupportedLanguage> = {
      'nl': 'nl', 'es': 'es', 'fr': 'fr', 'de': 'de'
    };
    const defaultLang: SupportedLanguage = langMap[langCode] || 'en';
    if (this._languageService.currentLanguage !== defaultLang) {
      this._languageService.setLanguage(defaultLang);
    }
    this.currentLanguage = this._languageService.currentLanguage;
  }

  private logCountryFromGeolocation(): void {
    if (!navigator.geolocation) {
      console.log('Geolocation not supported in this browser.');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        console.log('Geolocation coords:', { latitude, longitude });

        const url = `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`;
        fetch(url)
          .then((res) => res.json())
          .then((data) => {
            console.log('Geolocation country:', {
              countryName: data.countryName,
              countryCode: data.countryCode,
            });
            const countryCode = (data.countryCode || '').toUpperCase();
            const nlCountries = new Set(['NL', 'BE']);
            const esCountries = new Set(['ES', 'MX', 'AR', 'CO', 'CL', 'PE', 'VE', 'EC', 'GT', 'CU', 'BO', 'DO', 'HN', 'PY', 'SV', 'NI', 'CR', 'PA', 'UY', 'PR']);
            const frCountries = new Set(['FR', 'BE', 'CH', 'CA', 'DZ', 'MA', 'TN', 'SN', 'CI', 'CM', 'CD', 'CG', 'GA', 'GN', 'ML', 'NE', 'BF', 'BJ', 'TG', 'RW', 'BI', 'CF', 'DJ', 'KM', 'MG', 'MR', 'MU', 'SC']);
            const deCountries = new Set(['DE', 'AT', 'CH']);

            let defaultLang: SupportedLanguage = 'en';
            if (countryCode === 'IN') {
              defaultLang = 'en';
            } else if (nlCountries.has(countryCode)) {
              defaultLang = 'nl';
            } else if (esCountries.has(countryCode)) {
              defaultLang = 'es';
            } else if (frCountries.has(countryCode)) {
              defaultLang = 'fr';
            } else if (deCountries.has(countryCode)) {
              defaultLang = 'de';
            }
            if (this._languageService.currentLanguage !== defaultLang) {
              this._languageService.setLanguage(defaultLang);
            }
          })
          .catch((error) => {
            console.log('Geolocation reverse lookup failed:', error);
          });
      },
      (error) => {
        console.log('Geolocation permission denied or error:', error);
      },
      { enableHighAccuracy: false, timeout: 5000, maximumAge: 0 }
    );
  }

  t(key: string): string {
    return this._languageService.instant(key);
  }

  get orderedSignTags() {
    const order = [
      EnumSignTagTypes.INITIALS,
      EnumSignTagTypes.TEXT,
      EnumSignTagTypes.DATE,
      EnumSignTagTypes.CHECKBOX,
      EnumSignTagTypes.STAMP
    ];
    const tagMap = new Map(
      (this._signService.customSignTags || []).map((tag) => [tag.type, tag])
    );
    return order
      .map((type) => tagMap.get(type))
      .filter((tag): tag is NonNullable<typeof tag> => Boolean(tag));
  }

  get signToolbarItems() {
    const items = [
      { key: 'signature', kind: 'signature', labelKey: 'signature', icon: 'add' },
      { key: 'comment', kind: 'comment', labelKey: 'comment', icon: 'add_comment' },
      ...this.orderedSignTags.map((tag) => ({
        key: String(tag.type),
        kind: 'tag',
        labelKey: tag.type.toLowerCase(),
        icon: tag.icon,
        type: tag.type,
        dimensions: tag.dimensions,
      })),
    ].filter((item) =>
      this._signToolbarService.isToolbarItemVisible(item as SignToolbarListItem)
    );
    return items as Array<{
      key: string;
      kind: 'signature' | 'comment' | 'tag';
      labelKey: string;
      icon: string;
      type?: EnumSignTagTypes;
      dimensions?: any;
    }>;
  }

  get visibleToolbarItems() {
    const items = this.signToolbarItems;
    if (!this.isMobileView || this.isMobileToolbarExpanded) return items;
    return items.slice(0, 5);
  }

  get hiddenToolbarItemsCount(): number {
    if (!this.isMobileView || this.isMobileToolbarExpanded) return 0;
    return Math.max(0, this.signToolbarItems.length - 5);
  }

  get showMobileMoreToggle(): boolean {
    return this.isMobileView && (this.isMobileToolbarExpanded || this.hiddenToolbarItemsCount > 0);
  }

  toggleMobileToolbar(): void {
    this._signToolbarService.toggleExpanded();
  }

  onToolbarItemClick(item: SignToolbarAction): void {
    this._signToolbarService.emit(item);
  }

  onToolbarItemDragStart(event: DragEvent, item: SignToolbarAction): void {
    if (!event.dataTransfer) return;
    if (this.isToolbarItemDisabled(item)) return;
    if (item.kind === 'tag') {
      if (!item.type) return;
      const payload = JSON.stringify({ type: item.type, dimensions: item.dimensions });
      event.dataTransfer.setData('application/kzn-tag', payload);
      event.dataTransfer.effectAllowed = 'copy';
    } else if (item.kind === 'signature' || item.kind === 'comment') {
      const payload = JSON.stringify({ kind: item.kind });
      event.dataTransfer.setData('application/kzn-sign-placeholder', payload);
      event.dataTransfer.effectAllowed = 'copy';
    } else {
      return;
    }
    this._signService.notifyTagDragSuppressNext(true);
    const clear = () => {
      window.removeEventListener('dragend', clear);
      this._signService.notifyTagDragSuppressNext(false, 120);
    };
    window.addEventListener('dragend', clear);
  }

  toolbarIcon(item: { kind: "signature" | "comment" | "tag"; type?: EnumSignTagTypes }): string {
    if (item.kind === "signature") return "assets/signature-icon.png";
    if (item.kind === "comment") return "assets/comment-icon.png";
    switch (item.type) {
      case EnumSignTagTypes.INITIALS:
        return "assets/initials-icon.png";
      case EnumSignTagTypes.TEXT:
        return "assets/text-icon.png";
      case EnumSignTagTypes.DATE:
        return "assets/date-icon.png";
      case EnumSignTagTypes.CHECKBOX:
        return "assets/checklist-icon.png";
      case EnumSignTagTypes.STAMP:
        return "assets/stamp-icon.png";
      default:
        return "assets/signature-icon.png";
    }
  }

  toolbarIconActive(item: { kind: "signature" | "comment" | "tag"; type?: EnumSignTagTypes }): string {
    if (item.kind === "signature") return "assets/signature-active.png";
    if (item.kind === "comment") return "assets/comment-active.png";
    switch (item.type) {
      case EnumSignTagTypes.INITIALS:
        return "assets/initials-active.png";
      case EnumSignTagTypes.TEXT:
        return "assets/text-active.png";
      case EnumSignTagTypes.DATE:
        return "assets/date-active.png";
      case EnumSignTagTypes.CHECKBOX:
        return "assets/checkbox-active.png";
      case EnumSignTagTypes.STAMP:
        return "assets/stamp-active.png";
      default:
        return "assets/signature-active.png";
    }
  }

  onToolbarItemHover(key: string | null): void {
    this.hoveredToolbarKey = key;
  }

  isToolbarItemDisabled(item: { kind: 'signature' | 'comment' | 'tag' }): boolean {
    if (item.kind === 'comment') return false;
    return (this.signerRole || '').toLowerCase() !== EnumSignerRoleType.SIGNER.toLowerCase();
  }

  private updateMobileViewState(): void {
    this.isMobileView = window.innerWidth <= 1024;
  }

  ngOnDestroy(): void {
    this._toolbarSubscription.unsubscribe();
  }
}
